import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { WorkOrderState } from 'app/_models/work-order-state';
import { UserCompetency } from 'app/_models/competency.model';
import { SiteOwnerAuthorisation } from 'app/_models/site-owner-authorisation.model';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import Swal from 'sweetalert2';
import { EditScheduleModalComponent } from '../edit-schedule-modal/edit-schedule-modal.component';
import { CheckoutModalComponent } from '../checkout-modal/checkout-modal.component';
import { SessionsService } from 'app/_services/sessions.service';
import { NotificationService } from 'app/_services/notification.service';
import { CompetencyService } from 'app/_services/competency.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'action-bar',
  templateUrl: './action-bar.component.html',
})
export class ActionBarComponent implements OnInit, OnChanges {

  public WorkOrderStateType = WorkOrderState
  public checkOutType: any;
  public isAddWorker: any;
  private isAdmin: boolean;
  public isControlAdmin: boolean;
  public isAcknowledgeSurrender: boolean = false;
  public workerSession: any;
  public ownerWorkerCompanyName = '';
  public tocAuthorisationSummary = '';
  public tocWorkerCompetencies: UserCompetency[] = [];
  public tocWorkerContextLoading = false;
  public tocWorkerContextError: string = null;
  @Input() workOrderState: WorkOrderState;
  @Input() workOrder: any;
  @Input() siteId: number;

  @Input() openSessions?: any;
  @Input() surrenderedDetails?: any;
  @Input() suspendedDetails?: any;

  @Input() allowWorkOrderScheduling?: boolean;
  @Input() requiresToc?: boolean;

  @Output() refreshNeeded: EventEmitter<any> = new EventEmitter();

  @Input() userSession: any;
  @Input() leadWorkerSession: any;
  @Input() addSessionWorkers: any;

  private routeRole: any;

  constructor(
    private notificationService: NotificationService,
    private workOrderService: SiteWorkOrdersService,
    private modalService: NgbModal,
    private router: Router,
    private globals: Globals,
    private sessionsService: SessionsService,
    private competencyService: CompetencyService,
  ) {
    this.routeRole = this.globals.user.routeRole;
    this.isAdmin = (this.routeRole === 'admin' || this.routeRole === 'support');
    this.isControlAdmin = this.globals.user.role === 11;
  }

  ngOnInit() {
    this.ownerWorkerCompanyName = this.workOrder
      && this.workOrder.ownerWorkerUser
      && this.workOrder.ownerWorkerUser.maintenance
      && this.workOrder.ownerWorkerUser.maintenance.name
      ? this.workOrder.ownerWorkerUser.maintenance.name
      : '';

      this.shouldShowAcknowledgeSurrenderButton();
  }

  ngOnChanges(changes: SimpleChanges) {
    // TOC context data is separate API data from the main work-order payload.
    // We only react when inputs that can affect TOC panel visibility/context change.
    const hasTocRelevantChanges = !!(changes.workOrder || changes.workOrderState || changes.requiresToc);
    if (!hasTocRelevantChanges) {
      return;
    }
    this.shouldShowAcknowledgeSurrenderButton();

    const previousState = changes.workOrderState ? changes.workOrderState.previousValue : this.workOrderState;
    const previousRequiresToc = changes.requiresToc ? changes.requiresToc.previousValue : this.requiresToc;
    const wasVisible = this.canShowTocWorkerContext(previousState, previousRequiresToc);
    const isVisible = this.shouldShowTocWorkerContext();

    // If the TOC panel is no longer visible (state/permission changed),
    // clear stale TOC API data so hidden data is not carried forward.
    if (!isVisible) {
      this.resetTocWorkerContext();
      return;
    }

    // Reload TOC context only when:
    // 1) panel has just become visible, or
    // 2) navigated to a different work order (id changed).
    // This avoids refetching on routine 20s polling for the same work order.
    const enteredVisibleState = !wasVisible && isVisible;
    const workOrderIdChanged = !!(changes.workOrder
      && changes.workOrder.previousValue
      && changes.workOrder.currentValue
      && changes.workOrder.previousValue.id !== changes.workOrder.currentValue.id);

    if (enteredVisibleState || workOrderIdChanged) {
      this.loadTocWorkerContext();
    }
  }

  public approveWorkOrder() {
    const patch = {
      approvalStatus: 'APPROVED',
    };

    this.updateWorkOrder(patch).subscribe(() => {
      this.notificationService.notify('success', 'Updated!', 'Work Order has been updated.');
      this.refreshNeeded.emit();
    });
  }

  public releaseWorkOrder() {
    const patch = {
      released: true,
    };

    this.updateWorkOrder(patch).subscribe(() => {
      this.notificationService.notify('success', 'Updated!', 'Work Order has been updated.');
      this.refreshNeeded.emit();
    });
  }

  public async rejectWorkOrder() {
    const { value: comment } = await Swal.fire({
      titleText: 'Reason for rejection',
      input: 'textarea',
      inputValidator: (value) => {
        if (value.length === 0) {
          return 'You need to enter a comment when rejecting a work order'
        } else if (value.length > 255) {
          return 'Comments cannot be longer than 255 characters'
        }
      },
      inputAttributes: {
        'aria-label': 'Type your reason for rejecting the work order here'
      },
      showCancelButton: true,
    });

    if (comment) {
      const commentBody = {
        workOrderId: this.workOrder.id,
        comment: comment,
      };

      const patch = {
        approvalStatus: 'REJECTED',
      };

      this.updateWorkOrder(patch).subscribe(() => {
        this.workOrderService.addWorkOrderComment(commentBody).subscribe(() => {
          this.notificationService.notify('success', 'Updated!', 'Work Order has been updated.');
          this.refreshNeeded.emit();
        });
      });
    }
  }

  public editSchedule() {
    const modalOptions: NgbModalOptions = { centered: true };
    const modal = this.modalService.open(EditScheduleModalComponent, modalOptions);
    modal.componentInstance.workOrder = this.workOrder;

    modal.result.then(result => {
      if (result === 'refresh') {
        this.refreshNeeded.emit();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public openCheckOutModal() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static', size: 'lg' };
    const modal = this.modalService.open(CheckoutModalComponent, modalOptions);
    modal.componentInstance.workOrderState = this.workOrderState;
    modal.componentInstance.leadWorkerSession = this.leadWorkerSession;
    modal.componentInstance.addSessionWorkers = this.addSessionWorkers;

    modal.componentInstance.checkOutTypeSelected.subscribe(
      (data: { type: any; checkWorker: any; workerSession: any }) => {
        this.checkOutType = data.type;
        this.isAddWorker = data.checkWorker;
        this.workerSession = data.workerSession

        // both the modal and the following swal alert replace the scrollbar with some padding
        // need a short timeout to allow the the padding to reset before the swal, or padding gets compounded
        setTimeout(() => {
          this.checkOutWorkOrder(this.checkOutType, this.isAddWorker, this.workerSession);
        }, 0);
      });
  }

  public checkOutWorkOrder(checkOutType, isAddWorker, workerSession) {
    this.userSession.workOrderStatus = checkOutType;

    let title = '';
    let text = '';
    let toastSuccess = '';

    if (!isAddWorker) {

      switch (checkOutType) {
        case 'CLOSED':
          title = `Close work order?`
          toastSuccess = 'Workers checked out - work order CLOSED';

          if (this.workOrderState === 'Open') {
            text = 'This will check out all the active workers'
          } else {
            text = 'Are you sure you want to close this work order?'
          }
          break;
        case 'SURRENDERED':
          title = `Check Out and Surrender?`
          text = 'This will check out all the active workers <br> <b>The asset will remain locked</b> until the current lead worker resumes the work order.'
          toastSuccess = 'Workers checked out - work order set to SURRENDERED';
          break;
        case 'SUSPENDED':
          title = `Check Out and Suspend?`
          text = 'This will check out all the active workers'
          toastSuccess = 'Workers checked out - work order set to SUSPENDED';
          break;
        default:
          break;
      }

      Swal.fire({
        title: `${title}`,
        html: text,
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-danger',
        },
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.value) {
          this.userSession = this.userSession;
          this.addNote(checkOutType, toastSuccess)
        }
      });

    }
    else {
      Swal.fire({
        title: 'Check Out?',
        html: 'This will check out only the selected worker',
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-danger',
        },
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.value) {
          this.userSession = workerSession;
          toastSuccess = 'Worker has been successfully checked out'
          this.addNote(checkOutType, toastSuccess)
        }
      });
    }
  }

  public async addNote(checkOutType, toastSuccess) {

    let { value: notes } = await
      Swal.fire({
        titleText: 'Add note?',
        input: 'textarea',
        inputPlaceholder: 'Optional: Type your notes here...',
        showCancelButton: false,
      });
    if (notes) {
      this.userSession.notes = notes
    }
    if (checkOutType === 'CLOSED') {
      this.workOrderService.closeWorkOrder(this.workOrder.id).subscribe(() => {
        this.notificationService.notify('success', 'Closed!', `${toastSuccess}`);
        this.refreshNeeded.emit();
      });
    }

    if (checkOutType === 'SUSPENDED' || checkOutType === 'SURRENDERED') {
      this.sessionsService.checkoutWorker(this.userSession).subscribe(() => {
        this.notificationService.notify('success', 'Success!', `${toastSuccess}`);
        this.refreshNeeded.emit();
      });
    }
  }

  public showTocButtons(){
    return this.requiresToc
      && (this.routeRole === 'site-owner'|| this.isAdmin || this.isControlAdmin)
  }

  public shouldShowTocWorkerContext() {
    return this.canShowTocWorkerContext(this.workOrderState, this.requiresToc);
  }

  private shouldShowAcknowledgeSurrenderButton() {
      this.isAcknowledgeSurrender = !!(this.workOrder
        && (this.workOrder.tocStatus === 'XFER_SURRENDERED'
          || this.workOrder.tocStatus === 'XFER_RESUMED')
        && (this.routeRole === 'site-owner' || this.isAdmin || this.isControlAdmin));
  }

  public onCloseWorkOrder() {
    Swal.fire({
      title: 'Close Work Order?',
      text: 'Are you sure you want to close this work order?',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes',
    }).then((result) => {
      if (result.value) {
        this.workOrderService.closeWorkOrder(this.workOrder.id).subscribe(() => {
          this.notificationService.notify('success', 'Closed!', 'Work Order has been closed.');
          this.refreshNeeded.emit();
        });
      }
    });
  }

  public async overrideSurrender() {
    const { value: comment } = await Swal.fire({
      title: 'Attention',
      html: `<p>This will change the work order status to "suspended" and unlock the asset.</p>
          <p><b>Only proceed in order to allow another AT to take control of the surrendered work order.</b></p>`,
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, override and unlock',
      input: 'textarea',
      inputPlaceholder: 'Reason',
      inputAttributes: {
        'aria-label': 'Type your reason for overriding the surrendered work order here'
      },
      inputValidator: (value) => {
        if (value.length === 0) {
          return 'You need to enter a comment when overriding a surrendered work order'
        } else if (value.length > 255) {
          return 'Comments cannot be longer than 255 characters'
        }
      },
    });

    if (comment) {
      const commentBody = {
        workOrderId: this.workOrder.id,
        comment: comment,
      };

      this.workOrderService.overrideSurrenderedWorkOrder(this.workOrder.id).subscribe(() => {
        this.workOrderService.addWorkOrderComment(commentBody).subscribe(() => {
          this.notificationService.notify('success', 'Closed!', 'Work Order has been changed to suspended.');
          this.refreshNeeded.emit();
        });
      });
    }
  }

  public goToLiveSessions() {
    this.router.navigate(['/' + this.routeRole + '/live-sessions/']);
  }

  public goToCheckInPage() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.siteId + '/check-in/'], { queryParams: { workOrderId: this.workOrder.id }});
  }

  private updateWorkOrder(update: { approvalStatus?: string; released?: boolean; }) {
    const existingWorkOrder = {
      id: this.workOrder.id,
      released: this.workOrder.released,
      description: this.workOrder.description,
      approvalStatus: this.workOrder.approvalStatus,
      minimumPartySize: this.workOrder.minimumPartySize,
      scheduledStartTime: this.workOrder.scheduledStartTime,
      scheduledEndTime: this.workOrder.scheduledEndTime,
      checkOnSiteFormId: this.workOrder.checkOnSiteFormId,
      checkOffSiteFormId: this.workOrder.checkOffSiteFormId,
      exemptFromAssetScheduling: this.workOrder.exemptFromAssetScheduling,
    };

    const body = { ...existingWorkOrder, ...update };

    return this.workOrderService.updateWorkOrder(body);
  }

  private resetTocWorkerContext() {
    this.tocAuthorisationSummary = '';
    this.tocWorkerCompetencies = [];
    this.tocWorkerContextLoading = false;
    this.tocWorkerContextError = null;
  }

  private loadTocWorkerContext() {
    this.tocWorkerContextLoading = true;
    this.tocWorkerContextError = null;

    const workerId = this.workOrder.ownerWorkerUser.skylarkUserId;
    const siteOwnerId = this.workOrder.asset.site.siteOwner.siteOwnerUser.skylarkUserId;
    let hadEndpointError = false;

    forkJoin([
      this.competencyService.loadUserCompetencyStatus(workerId, siteOwnerId).pipe(
        catchError(() => {
          hadEndpointError = true;
          this.notificationService.notify('danger', 'Error', 'Could not load TOC worker competencies.');
          return of([]);
        })
      ),
      this.competencyService.getAuthorisationNotes(workerId, siteOwnerId).pipe(
        catchError(() => {
          hadEndpointError = true;
          this.notificationService.notify('danger', 'Error', 'Could not load TOC competency restrictions.');
          return of([]);
        })
      ),
    ]).subscribe(
      ([workerCompetencies, authorisationNotes]: [UserCompetency[], SiteOwnerAuthorisation[]]) => {
        this.tocWorkerCompetencies = workerCompetencies.filter((competency) => competency.hasCompetencyApproval);
        this.tocAuthorisationSummary = authorisationNotes.length && authorisationNotes[0].competencyNotes ? authorisationNotes[0].competencyNotes : '';
        this.tocWorkerContextError = hadEndpointError ? 'Some TOC competency details could not be loaded.' : null;
        this.tocWorkerContextLoading = false;
      },
      () => {
        this.tocWorkerContextError = 'Unable to load TOC worker competency details.';
        this.tocWorkerContextLoading = false;
      }
    );
  }

  private canShowTocWorkerContext(state: WorkOrderState, requiresToc: boolean): boolean {
    if (!requiresToc || !(this.routeRole === 'site-owner' || this.isAdmin || this.isControlAdmin)) {
      return false;
    }

    return state === WorkOrderState.PendingXfer
      || state === WorkOrderState.XferAcknowledged
      || state === WorkOrderState.Open
      || state === WorkOrderState.PendingReturn
      || state === WorkOrderState.ReturnAcknowledged;
  }

  public async acknowledgeWorkOrderTransfer(workOrder: any, xferToWorker: boolean) {
    let tocStatus = 'RETURNED';
    let tocTitleText = 'ACKNOWLEDGE TRANSFER OF CONTROL TO OC';
    const tocIntroText = 'Are you sure you want to confirm the request?';
    const tocInstructionText = xferToWorker
      ? 'IF YES, DRESS ASSET AS TRANSFERRED IN POWERON'
      : 'IF YES, DRESS ASSET AS RETURNED IN POWERON';
    const tocConfirmText = 'If correct, press "OK"';
    const ownerUser = workOrder && workOrder.ownerWorkerUser ? workOrder.ownerWorkerUser : null;
    const ownerFirstName = ownerUser && ownerUser.firstName ? ownerUser.firstName : '';
    const ownerLastName = ownerUser && ownerUser.lastName ? ownerUser.lastName : '';
    const ownerName = (ownerFirstName + ' ' + ownerLastName).trim();
    const asset = workOrder && workOrder.asset ? workOrder.asset : null;
    const site = asset && asset.site ? asset.site : null;
    if(xferToWorker){
          tocStatus = 'XFER'
          tocTitleText = 'ACKNOWLEDGE TRANSFER OF CONTROL TO ' +  (ownerName || 'WORKER');
    }
    const assetName = asset && (asset.assetName || asset.name) ? (asset.assetName || asset.name) : 'Asset Name';
    const siteName = site && site.name ? site.name : 'Site Name';
    const workOrderNumber = workOrder && workOrder.id != null ? workOrder.id : 'WO Number';
    const workOrderDescription = workOrder && workOrder.description ? workOrder.description : 'Work Order Short Description';
    const assetLine = assetName + ' at ' + siteName;
    const workOrderLine = workOrderNumber + ' - ' + workOrderDescription;
    const bodyHtml = `
      <div style="text-align:left;">
        <p style="margin:0 0 6px;" data-toc="intro"></p>
        <ul style="margin:0 0 10px; padding-left:1.2em; line-height:1.4;">
          <li data-toc="assetLine"></li>
          <li data-toc="workOrderLine"></li>
        </ul>
        <p style="margin:0 0 6px; font-weight:700;" data-toc="instruction"></p>
        <p style="margin:0;" data-toc="confirm"></p>
      </div>
    `;
    const swalOptions: any = {
      titleText: `${tocTitleText}`,
      html: bodyHtml,
      type: 'info',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-success',
      },
      confirmButtonText: 'OK',
      cancelButtonText: 'CANCEL',
    };
    swalOptions.onOpen = () => {
      const content = Swal.getContent();
      if (!content) {
        return;
      }
      const setText = (selector: string, value: string) => {
        const element = content.querySelector(selector);
        if (element) {
          (element as HTMLElement).textContent = value;
        }
      };
      setText('[data-toc="intro"]', tocIntroText);
      setText('[data-toc="assetLine"]', assetLine);
      setText('[data-toc="workOrderLine"]', workOrderLine);
      setText('[data-toc="instruction"]', tocInstructionText);
      setText('[data-toc="confirm"]', tocConfirmText);
    };
    const { value: success } = await Swal.fire(swalOptions);



    if (success) {
      console.log('Acknowledging TOC for work order:', workOrder.id);
      const acknowledgement = {
        tocStatus: tocStatus,
      };

      this.workOrderService.updateTocStatus(acknowledgement, workOrder.id).subscribe(() => {
        this.notificationService.notify('success', 'Updated!', 'Work Order Transfer of Control Acknowledged!');
        this.refreshNeeded.emit();
      });
    }
  }

  public async rejectWorkOrderTransfer(workOrder: any, xferToWorker: boolean) {
    let tocStatus = 'RETURN_REJECTED';
    let tocTitleText = 'REJECT TRANSFER OF CONTROL TO OC';
    const tocIntroText = 'Are you sure you want to reject the request?';
    const tocConfirmText = 'If correct, press "REJECT"';
    const ownerUser = workOrder && workOrder.ownerWorkerUser ? workOrder.ownerWorkerUser : null;
    const ownerFirstName = ownerUser && ownerUser.firstName ? ownerUser.firstName : '';
    const ownerLastName = ownerUser && ownerUser.lastName ? ownerUser.lastName : '';
    const ownerName = (ownerFirstName + ' ' + ownerLastName).trim();
    const asset = workOrder && workOrder.asset ? workOrder.asset : null;
    const site = asset && asset.site ? asset.site : null;
    if(xferToWorker){
          tocStatus = 'XFER_REJECTED'
          tocTitleText = 'REJECT TRANSFER OF CONTROL TO ' +  (ownerName || 'WORKER');
    }
    const assetName = asset && (asset.assetName || asset.name) ? (asset.assetName || asset.name) : 'Asset Name';
    const siteName = site && site.name ? site.name : 'Site Name';
    const workOrderNumber = workOrder && workOrder.id != null ? workOrder.id : 'WO Number';
    const workOrderDescription = workOrder && workOrder.description ? workOrder.description : 'Work Order Short Description';
    const assetLine = assetName + ' at ' + siteName;
    const workOrderLine = workOrderNumber + ' - ' + workOrderDescription;
    const bodyHtml = `
      <div style="text-align:left;">
        <p style="margin:0 0 6px;" data-toc="intro"></p>
        <ul style="margin:0 0 10px; padding-left:1.2em; line-height:1.4;">
          <li data-toc="assetLine"></li>
          <li data-toc="workOrderLine"></li>
        </ul>
        <p style="margin:0;" data-toc="confirm"></p>
      </div>
    `;
    const { value: success } = await Swal.fire({
      titleText: `${tocTitleText}`,
      html: bodyHtml,
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-success',
      },
      confirmButtonText: 'REJECT',
      cancelButtonText: 'CANCEL',
      onOpen: () => {
        const content = Swal.getContent();
        if (!content) {
          return;
        }
        const setText = (selector: string, value: string) => {
          const element = content.querySelector(selector);
          if (element) {
            (element as HTMLElement).textContent = value;
          }
        };
        setText('[data-toc="intro"]', tocIntroText);
        setText('[data-toc="assetLine"]', assetLine);
        setText('[data-toc="workOrderLine"]', workOrderLine);
        setText('[data-toc="confirm"]', tocConfirmText);
      },
    });



    if (success) {
      console.log('Rejecting TOC for work order:', workOrder.id);
      const acknowledgement = {
        tocStatus: tocStatus,
      };

      this.workOrderService.updateTocStatus(acknowledgement, workOrder.id).subscribe(() => {
        this.notificationService.notify('success', 'Updated!', 'Work Order Transfer of Control Rejected!');
        this.refreshNeeded.emit();
      });
    }
  }

  public async acknowledgeWorkOrderSurrender(workOrder: any) {
    const isResumed = workOrder && workOrder.tocStatus === 'XFER_RESUMED';

    // Safely extract nested properties using && and || (Compatible with TS 2.7)
    const ownerUser = workOrder ? workOrder.ownerWorkerUser : null;
    const ownerFirstName = ownerUser ? (ownerUser.firstName || '') : '';
    const ownerLastName = ownerUser ? (ownerUser.lastName || '') : '';
    const ownerName = (ownerFirstName + ' ' + ownerLastName).trim();

    const asset = workOrder ? workOrder.asset : null;
    const site = asset ? asset.site : null;

    const assetName = asset ? (asset.assetName || asset.name || 'Asset Name') : 'Asset Name';
    const siteName = site ? (site.name || 'Site Name') : 'Site Name';

    const ownerLine = 'Work party leader: ' + ownerName;
    let tocIntroText = assetName + ' at ' + siteName + ' remains transferred with work SURRENDERED';
    let tocTitleText = 'CONFIRMED - WORK SURRENDERED';

    if (isResumed) {
        tocTitleText = 'CONFIRMED - SURRENDERED WORK RESUMED';
        tocIntroText = 'Surrendered work has resumed on ' + assetName + ' at ' + siteName;
    }

    const workOrderNumber = workOrder && workOrder.id != null
        ? workOrder.id + (workOrder.externalWorkOrderId ? ' (' + workOrder.externalWorkOrderId + ')' : '')
        : 'WO Number';

    const workOrderDescription = workOrder && workOrder.description ? workOrder.description : 'Work Order Short Description';
    const workOrderLine = 'WO Number: ' + workOrderNumber + ' - ' + workOrderDescription;

    const bodyHtml = `
      <div style="text-align:left;">
        <p style="margin:0 0 6px;" data-toc="intro"></p>
        <ul style="margin:0 0 10px; padding-left:1.2em; line-height:1.4;">
          <li data-toc="ownerLine"></li>
          <li data-toc="workOrderLine"></li>
          ${!isResumed ? '<li data-toc="localControlLine"></li>' : ''}
        </ul>
        <p style="margin:0 0 2px; font-weight:600;" data-toc="powerOnText"></p>
        <p style="margin:0;" data-toc="confirm"></p>
      </div>
    `;

    const swalOptions: any = {
        titleText: tocTitleText,
        html: bodyHtml,
        type: 'info',
        customClass: {
            confirmButton: 'confirm-success',
        },
        confirmButtonText: 'OK',
    };

    swalOptions.onOpen = () => {
        const content = Swal.getContent();
        if (!content) {
            return;
        }

        const setText = (selector: string, value: string) => {
            const element = content.querySelector(selector);
            if (element) {
                (element as HTMLElement).textContent = value;
            }
        };

        setText('[data-toc="intro"]', tocIntroText);
        setText('[data-toc="ownerLine"]', ownerLine);
        setText('[data-toc="workOrderLine"]', workOrderLine);

        if (!isResumed) {
            setText('[data-toc="localControlLine"]', 'Local control of the asset is retained');
        }

        setText('[data-toc="powerOnText"]', '**CHECK ASSET IS DRESSED APPROPRIATELY IN POWERON**');
        setText('[data-toc="confirm"]', 'Press "OK" to acknowledge');
    };

    const result = await Swal.fire(swalOptions);
    const success = result.value;

    if (success) {
        console.log('Acknowledging Surrender for work order:', workOrder.id);
        const acknowledgement = { tocStatus: 'XFER' };

        // Using positional arguments for subscribe to ensure compatibility with older RxJS versions (RxJS 5/6)
        this.workOrderService.updateTocStatus(acknowledgement, workOrder.id).subscribe(
            () => {
                this.isAcknowledgeSurrender = false;
                if (this.workOrder) {
                    this.workOrder.tocStatus = 'XFER';
                }
                this.notificationService.notify('success', 'Updated!', 'Work Order Surrender Acknowledged!');
                this.refreshNeeded.emit();
            },
            (err) => {
                console.error('Failed to acknowledge surrender:', err);
                this.notificationService.notify('danger', 'Error', 'Failed to acknowledge surrender!');
            }
        );
    }
}

}
